import { useState, useEffect, useCallback } from 'react';
import api from '../../hooks/useApi';
import { fmtDateTimeLong } from '../../utils/dates.js';
import { useToast } from '../ui/Toast';

export default function BackupsPage() {
  const { toast } = useToast();
  const [backups, setBackups] = useState(null);
  const [expanded, setExpanded] = useState(null); // date | null
  const [manifest, setManifest] = useState(null);
  const [manifestLoading, setManifestLoading] = useState(false);
  const [running, setRunning] = useState(false);

  const load = useCallback(() => api.get('backups').then(setBackups).catch(() => setBackups([])), []);
  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunning(true);
    try {
      await api.post('backups/run', {});
      toast.success('Backup started — this runs in the background and usually takes a minute or two. Refresh the list shortly to see it.');
    } catch (e) {
      toast.error(e.message || 'Failed to start backup');
    }
    setRunning(false);
  };

  const toggleExpand = async (date) => {
    if (expanded === date) { setExpanded(null); return; }
    setExpanded(date);
    setManifest(null);
    setManifestLoading(true);
    try { setManifest(await api.get(`backups/${date}`)); }
    catch { setManifest(null); }
    setManifestLoading(false);
  };

  return (
    <div className="fade-in p-4">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-base font-extrabold text-navy">Database Backups</h1>
          <div className="text-[9px] text-gray-500 mt-0.5">
            The whole database is exported overnight to a separate R2 bucket, independent of Cloudflare D1's own
            point-in-time recovery window. Kept for {90} days on a rolling basis, then automatically deleted.
            This is a safety net, not a self-service restore tool — if you ever need to restore from one of these,
            that's a manual/assisted process, not a button here.
          </div>
        </div>
        <button onClick={runNow} disabled={running}
          className="px-3 py-1.5 text-[10px] font-bold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-40 flex-shrink-0 whitespace-nowrap">
          {running ? 'Starting…' : 'Back up now'}
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded overflow-hidden">
        {backups === null ? (
          <div className="text-center py-8 text-[10px] text-gray-400">Loading…</div>
        ) : backups.length === 0 ? (
          <div className="text-center py-8 text-[10px] text-gray-400">
            No backups yet — either the nightly cron hasn't run since this was set up, or click "Back up now" above.
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold">Date</th>
                <th className="px-3 py-1.5 text-right text-[8px] uppercase text-gray-500 font-bold">Tables</th>
                <th className="px-3 py-1.5 text-right text-[8px] uppercase text-gray-500 font-bold">Rows</th>
                <th className="px-3 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold">Status</th>
                <th className="px-3 py-1.5 text-right text-[8px] uppercase text-gray-500 font-bold"></th>
              </tr>
            </thead>
            <tbody>
              {backups.map(b => (
                <>
                  <tr key={b.date} className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => toggleExpand(b.date)}>
                    <td className="px-3 py-2 text-[9px] font-semibold text-navy">{b.date}</td>
                    <td className="px-3 py-2 text-[9px] text-right">{b.manifest_missing ? '—' : b.tables}</td>
                    <td className="px-3 py-2 text-[9px] text-right">{b.manifest_missing ? '—' : b.total_rows.toLocaleString()}</td>
                    <td className="px-3 py-2 text-[9px]">
                      {b.manifest_missing ? (
                        <span className="px-1.5 py-0.5 rounded text-[8px] font-bold bg-red-100 text-red-600">MANIFEST MISSING</span>
                      ) : b.partial ? (
                        <span className="px-1.5 py-0.5 rounded text-[8px] font-bold bg-amber-100 text-amber-700" title={`Ran out of time — ${b.remaining_tables.length} table(s) not exported: ${b.remaining_tables.join(', ')}`}>
                          PARTIAL — {b.remaining_tables.length} TABLE(S) NOT DONE
                        </span>
                      ) : b.failed_tables.length ? (
                        <span className="px-1.5 py-0.5 rounded text-[8px] font-bold bg-red-100 text-red-600">{b.failed_tables.length} TABLE(S) FAILED</span>
                      ) : (
                        <span className="px-1.5 py-0.5 rounded text-[8px] font-bold bg-emerald-100 text-emerald-700">OK</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-[9px] text-gray-400">{expanded === b.date ? '▼' : '▶'}</td>
                  </tr>
                  {expanded === b.date && (
                    <tr key={`${b.date}-detail`}>
                      <td colSpan={5} className="px-3 py-3 bg-gray-50 border-t border-b border-gray-100">
                        {manifestLoading ? (
                          <div className="text-[9px] text-gray-400">Loading…</div>
                        ) : !manifest ? (
                          <div className="text-[9px] text-red-500">Couldn't load this backup's manifest.</div>
                        ) : (
                          <>
                            <div className="text-[8px] text-gray-400 mb-2">
                              Started {fmtDateTimeLong(manifest.started_at)} · Finished {manifest.finished_at ? fmtDateTimeLong(manifest.finished_at) : '—'}
                            </div>
                            {manifest.partial && (
                              <div className="text-[8px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2">
                                Ran out of time before finishing — {(manifest.remaining_tables || []).length} table(s) never attempted:{' '}
                                {(manifest.remaining_tables || []).join(', ')}. Click "Back up now" to retry.
                              </div>
                            )}
                            <div className="overflow-x-auto max-h-64 overflow-y-auto">
                              <table className="w-full">
                                <thead><tr>
                                  <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-400 font-bold">Table</th>
                                  <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-400 font-bold">Rows</th>
                                  <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-400 font-bold"></th>
                                  <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-400 font-bold"></th>
                                </tr></thead>
                                <tbody>
                                  {(manifest.tables || []).map(t => (
                                    <tr key={t.table} className="border-t border-gray-100">
                                      <td className="px-2 py-1 text-[9px] font-mono">{t.table}</td>
                                      <td className="px-2 py-1 text-[9px] text-right">{t.rows.toLocaleString()}</td>
                                      <td className="px-2 py-1 text-[9px]">
                                        {!t.success && <span className="text-red-500" title={t.error}>✗ {t.error}</span>}
                                      </td>
                                      <td className="px-2 py-1 text-right">
                                        {t.success && (
                                          <a href={`/api/backups/${b.date}/${t.table}`} className="text-[8px] font-semibold text-blue-600 hover:underline">
                                            Download ↓
                                          </a>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </>
                        )}
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
